import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { BlogPost as BlogPostType } from '../../types/blog';
import { fetchBlogPostBySlug, formatPublishDate } from '../../utils/blogUtils';
import LoadingAnimation from '../LoadingAnimation';
import LikeButton from './LikeButton';

// Global deduplication cache to prevent multiple tracking calls
const trackingCache = new Set<string>();

interface BlogPostProps {
  darkMode?: boolean;
}

const BlogPost: React.FC<BlogPostProps> = ({ darkMode = false }) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasTracked, setHasTracked] = useState(false);

  useEffect(() => {
    if (slug) {
      setHasTracked(false); // Reset tracking flag for new post
      loadBlogPost(slug);
    }
  }, [slug]);

  // Track page view when post loads (only once per page load)
  useEffect(() => {
    console.log('📝 BlogPost useEffect triggered:', { 
      post: !!post, 
      loading, 
      error, 
      hasTracked, 
      postId: post?.sys?.id 
    });
    if (post && !loading && !error && !hasTracked) {
      console.log('🎯 Calling trackPageView for post:', post.sys.id);
      trackPageView(post.sys.id);
      setHasTracked(true);
    }
  }, [post, loading, error, hasTracked]);

  // Generate session ID for analytics tracking
  const getSessionId = () => {
    let sessionId = localStorage.getItem('blog_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('blog_session_id', sessionId);
    }
    return sessionId;
  };

  // Track page view
  const trackPageView = async (blogPostId: string) => {
    // Add a timestamp and call counter for debugging
    const callId = Date.now() + '-' + Math.random().toString(36).substr(2, 5);
    const sessionId = getSessionId();
    const cacheKey = `${blogPostId}-${sessionId}`;
    
    console.log(`🚨 TRACKING CALL #${callId} STARTED for post:`, blogPostId);
    console.log(`🔍 Cache check - Key: ${cacheKey}, Already tracked: ${trackingCache.has(cacheKey)}`);
    
    // Check if we've already tracked this post for this session
    if (trackingCache.has(cacheKey)) {
      console.log(`🛑 Call #${callId} - SKIPPED: Already tracked this post for this session`);
      return;
    }
    
    // Add to cache immediately to prevent concurrent calls
    trackingCache.add(cacheKey);
    console.log(`✅ Added to cache: ${cacheKey}`);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      console.log(`🔍 Call #${callId} - Attempting to track page view:`, {
        blogPostId,
        apiUrl,
        sessionId,
        trackingUrl: `${apiUrl}/api/blog/track`,
        timestamp: new Date().toISOString()
      });
      
      const response = await fetch(`${apiUrl}/api/blog/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          blog_post_id: blogPostId,
          event_type: 'page_view',
          session_id: sessionId,
          referrer: document.referrer || null,
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Call #${callId} - Analytics tracking successful:`, result);
      } else {
        console.warn(`⚠️ Call #${callId} - Analytics tracking failed with status:`, response.status, response.statusText);
        // Remove from cache if the request failed so it can be retried
        trackingCache.delete(cacheKey);
      }
    } catch (error) {
      // Log error for debugging but don't crash the app
      console.warn(`❌ Call #${callId} - Analytics tracking error:`, error);
      // Remove from cache if the request failed so it can be retried
      trackingCache.delete(cacheKey);
    }
    
    console.log(`🏁 TRACKING CALL #${callId} FINISHED`);
  };

  const loadBlogPost = async (postSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      const blogPost = await fetchBlogPostBySlug(postSlug);
      
      if (!blogPost) {
        setError('Blog post not found');
      } else {
        setPost(blogPost);
      }
    } catch (err) {
      console.error('Error loading blog post:', err);
      setError('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingAnimation onComplete={() => {}} />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Blog Post Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'The blog post you\'re looking for doesn\'t exist.'}</p>
            <button
              onClick={() => navigate('/blog')}
              className="px-6 py-2 bg-[#00A0DF] text-white rounded-lg hover:bg-[#00A0DF]/90 transition-colors"
            >
              ← Back to Blog
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { title, shortDescription, featuredImage, publishedDate, author, content } = post.fields;
  
  // Debug: Check content structure
  console.log('DEBUG BlogPost: Content structure:', {
    hasContent: !!content,
    contentType: typeof content,
    contentValue: content,
    isObject: typeof content === 'object',
    hasNodeType: content?.nodeType,
    contentNodes: content?.content?.length || 0
  });

  // Rich text rendering options to handle embedded entries
  const renderOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
        const { sys, fields } = node.data.target;
        
        // Handle componentRichImage
        if (sys.contentType?.sys?.id === 'componentRichImage') {
          const { image, caption, fullWidth } = fields;
          
          if (image && image.fields && image.fields.file) {
            return (
              <div className={`my-8 ${fullWidth ? 'w-full' : 'max-w-2xl mx-auto'}`}>
                <img
                  src={`https:${image.fields.file.url}`}
                  alt={image.fields.title || caption || 'Embedded image'}
                  className="w-full rounded-lg shadow-lg"
                />
                {caption && (
                  <p className={`text-sm mt-2 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {caption}
                  </p>
                )}
              </div>
            );
          }
        }
        
        // Handle other embedded entry types if needed
        return <div>Unsupported embedded content</div>;
      },
      [INLINES.EMBEDDED_ENTRY]: (node: any) => {
        // Handle inline embedded entries if needed
        return <span>Inline embedded content</span>;
      },
    },
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/blog')}
            className={`mb-6 px-4 py-2 rounded-lg transition-colors ${
              darkMode
                ? 'bg-gray-700 text-white hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            ← Back to Blog
          </button>

          {/* Featured Image */}
          {featuredImage && featuredImage.fields && (
            <div className="mb-8 overflow-hidden rounded-lg">
              <img
                src={`https:${featuredImage.fields.file?.url}?w=800&h=400&fit=fill&f=center`}
                alt={(featuredImage.fields.title as string) || title || 'Blog post image'}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>
          )}

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {title || 'Untitled Post'}
          </h1>

          {/* Subtitle */}
          {shortDescription && (
            <p className={`text-xl mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {shortDescription}
            </p>
          )}

          {/* Meta Information */}
          <div className={`flex flex-wrap items-center justify-between gap-4 text-sm border-b pb-6 mb-8 ${
            darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
          }`}>
            <div className="flex flex-wrap items-center gap-4">
              {publishedDate && (
                <span>📅 {formatPublishDate(publishedDate)}</span>
              )}
              {author && author.fields && (
                <span>✍️ by {author.fields.name}</span>
              )}
            </div>
            
            {/* Like Button */}
            <div className="flex-shrink-0">
              <LikeButton
                blogPostId={post.sys.id}
                size="medium"
                className="shadow-sm"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="blog-content">
          <style dangerouslySetInnerHTML={{
            __html: `
              .blog-content p {
                margin-bottom: 1.5rem;
                line-height: 1.7;
                color: ${darkMode ? '#d1d5db' : '#374151'};
              }
              .blog-content h1, .blog-content h2, .blog-content h3, .blog-content h4, .blog-content h5, .blog-content h6 {
                margin-top: 2rem;
                margin-bottom: 1rem;
                font-weight: 600;
                color: ${darkMode ? '#ffffff' : '#111827'};
              }
              .blog-content h1 { font-size: 2.25rem; }
              .blog-content h2 { font-size: 1.875rem; }
              .blog-content h3 { font-size: 1.5rem; }
              .blog-content ul, .blog-content ol {
                margin-bottom: 1.5rem;
                padding-left: 2rem;
                list-style-position: outside;
              }
              .blog-content ul {
                list-style-type: disc;
              }
              .blog-content ol {
                list-style-type: decimal;
              }
              .blog-content li {
                margin-bottom: 0.5rem;
                line-height: 1.6;
                display: list-item;
              }
              .blog-content a {
                color: #3b82f6;
                text-decoration: underline;
              }
              .blog-content a:hover {
                color: #1d4ed8;
              }
              .blog-content blockquote {
                border-left: 4px solid #e5e7eb;
                padding-left: 1rem;
                margin: 1.5rem 0;
                font-style: italic;
                color: ${darkMode ? '#9ca3af' : '#6b7280'};
              }
              .blog-content img {
                max-width: 100%;
                height: auto;
                margin: 1.5rem 0;
                border-radius: 0.5rem;
              }
            `
          }} />
          {content ? (
            documentToReactComponents(content, renderOptions)
          ) : (
            <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
              No content available for this post.
            </p>
          )}
        </div>

        {/* Back to Blog */}
        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/blog')}
            className="px-6 py-3 bg-[#00A0DF] text-white rounded-lg hover:bg-[#00A0DF]/90 transition-colors"
          >
            ← Back to Blog
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;